// src/features/deadlines/components/MonatsuebersichtQrVersandModal.tsx
//
// Modal für die Frist "OPPM Monatsübersicht QR-Code-Versand".
//
// Schwester-Komponente des OppmQrVersandModal, zugeschnitten auf die
// Monatsübersichten:
//   - Empfänger-Auswahl aus customer_contacts mit
//     receives_monatsuebersicht_qr = true UND is_active = true
//   - kein PDF-Upload, keine manuellen Anhänge (der Edge-Handler baut das ZIP
//     aus den bereits im Bucket liegenden Monatsübersichten), keine To-Do-Sektion
//   - Absender-Auswahl inkl. Foto-Gate und Zugangs-/DATEV-Token-Gate wie im
//     OPPM-Modal
//
// Vormonat-Modell: Die Frist gehört zum Arbeitsmonat (Prop month/year), der
// Versand betrifft die Monatsübersichten des Vormonats. Der Pending-Task wird
// im Hook mit der Vormonats-Periode angelegt; hier dient der Vormonat nur der
// Anzeige.
//
// Wartemechanismus: Nach "Bereitstellen" bleibt der Task offen, bis das externe
// Apps-Script sein Abschluss-Manifest für die Periode geschrieben hat
// (vollstaendig=true). Erst dann stempelt und versendet der Handler. Die Kachel
// zeigt bis dahin den gelben "beauftragt/wartend"-Zustand.

import React, { useState, useEffect } from 'react';
import { X, AlertCircle, ShieldAlert, Lock, Users } from 'lucide-react';
import { Customer, CustomerContact } from '../../../shared/types';
import { EmailSenderIdentity } from '../../../services/email/types';
import { SenderSelection } from './SenderSelection';
import { CustomMessageInput } from './CustomMessageInput';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSenderManagement } from '../hooks/useSenderManagement';
import { customerContactService } from '../../../services/customer/CustomerContactService';
import { getMonatsuebersichtQrPeriode } from '../../../shared/utils/dateUtils';
import { supabase } from '../../../shared/services/supabase';

interface MonatsuebersichtQrVersandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRelease: (
    contactId: string,
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string
  ) => Promise<void>;
  onMarkDone: () => Promise<void>;
  customer: Customer;
  month: number; // Arbeitsmonat des Standorts
  year: number;
  // Abrechnungsperiode der Frist, vom Aufrufer durchgereicht. Siehe
  // OppmQrVersandModal. Ohne die Angabe greift das alte Vormonat-Modell.
  periodeJahr?: number;
  periodeMonat?: number;
}

export const MonatsuebersichtQrVersandModal: React.FC<MonatsuebersichtQrVersandModalProps> = ({
  isOpen,
  onClose,
  onRelease,
  onMarkDone,
  customer,
  month,
  year,
  periodeJahr,
  periodeMonat
}) => {
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  const [isReleasing, setIsReleasing] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customMessage, setCustomMessage] = useState('');

  const [hasSetupPending, setHasSetupPending] = useState(false);
  const [accessCheckLoading, setAccessCheckLoading] = useState(false);
  const [datevTokenOk, setDatevTokenOk] = useState(false);
  const [datevTokenLoading, setDatevTokenLoading] = useState(false);
  const [datevProbeStatus, setDatevProbeStatus] = useState<string | null>(null);

  const { user } = useAuth();

  const {
    senders,
    selectedSender,
    setSelectedSender,
    loading: sendersLoading,
    error: sendersError,
    photoValidationErrors,
    senderIdentityUnconfirmed
  } = useSenderManagement(isOpen, user?.email);

  // Empfänger laden beim Öffnen
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const loadContacts = async () => {
      setContactsLoading(true);
      setSelectedContactId(null);
      setError(null);

      const mueContacts = await customerContactService.getMonatsuebersichtQrContacts(customer.id);

      if (cancelled) return;

      setContacts(mueContacts);
      if (mueContacts.length === 1) {
        setSelectedContactId(mueContacts[0].id);
      }
      setContactsLoading(false);
    };

    loadContacts();

    return () => {
      cancelled = true;
    };
  }, [isOpen, customer.id]);

  // Zugangs-Prüfung pro gewähltem Empfänger
  useEffect(() => {
    if (!selectedContactId) {
      setHasSetupPending(false);
      setAccessCheckLoading(false);
      return;
    }

    let cancelled = false;
    setAccessCheckLoading(true);

    customerContactService.hasPasswordSetupPending(selectedContactId)
      .then(pending => {
        if (cancelled) return;
        setHasSetupPending(pending);
        setAccessCheckLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHasSetupPending(false);
        setAccessCheckLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedContactId]);

  // DATEV-Token-Gate (identische RPC wie OPPM-Modal)
  useEffect(() => {
    if (!isOpen) return;

    const ber = customer.beraternummer;
    const man = customer.mandantennummer;

    if (!ber || !man) {
      setDatevTokenOk(false);
      setDatevProbeStatus(null);
      setDatevTokenLoading(false);
      return;
    }

    let cancelled = false;
    setDatevTokenLoading(true);

    supabase
      .rpc('check_datev_token_status', {
        p_beraternummer: ber,
        p_mandantennummer: man
      })
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) {
          console.error('[MonatsuebersichtQrVersandModal] check_datev_token_status fehlgeschlagen:', rpcError);
          setDatevTokenOk(false);
          setDatevProbeStatus(null);
        } else {
          const row = Array.isArray(data) ? data[0] : data;
          setDatevTokenOk(row?.has_valid_token === true);
          setDatevProbeStatus(row?.probe_status ?? null);
        }
        setDatevTokenLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, customer.beraternummer, customer.mandantennummer]);

  if (!isOpen) return null;

  const getMonthName = (monthNumber: number): string => {
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    return months[monthNumber - 1] || '';
  };

  const periode = (periodeJahr !== undefined && periodeMonat !== undefined)
    ? { year: periodeJahr, month: periodeMonat }
    : getMonatsuebersichtQrPeriode(year, month);

  const selectedContact = contacts.find(c => c.id === selectedContactId) || null;
  const hasPassword = selectedContact
    ? !!selectedContact.secure_download_passwort && selectedContact.secure_download_passwort.trim() !== ''
    : false;
  const hasToken = selectedContact
    ? !!selectedContact.secure_download_token && selectedContact.secure_download_token.trim() !== ''
    : false;

  const canAccess = hasPassword || hasSetupPending;
  const datevGateApplicable = !!customer.beraternummer && !!customer.mandantennummer;

  const handleSenderChange = (sender: EmailSenderIdentity) => {
    setSelectedSender(sender);
  };

  const handleForceRelogin = () => {
    localStorage.removeItem('authToken');
    window.location.reload();
  };

  const handleRelease = async () => {
    if (contacts.length === 0) {
      setError('Es ist kein Empfänger mit der Berechtigung "Erhält OPPM-Monatsübersichten mit QR-Code" hinterlegt. Bitte legen Sie zuerst einen Ansprechpartner mit dieser Berechtigung an.');
      return;
    }

    if (!selectedContactId || !selectedContact) {
      setError('Bitte wählen Sie einen Empfänger aus.');
      return;
    }

    if (!canAccess) {
      setError('Der gewählte Empfänger hat weder ein Passwort gesetzt noch eine gültige Passwort-Einladung offen. Bitte senden Sie zuerst eine Passwort-Einladung in den Stammdaten des Ansprechpartners.');
      return;
    }

    if (!hasToken) {
      setError('Der gewählte Empfänger hat keinen Zugangs-Token. Bitte sprechen Sie den Administrator an — der Token sollte beim Anlegen automatisch erzeugt werden.');
      return;
    }

    if (!customer.beraternummer || !customer.mandantennummer) {
      setError('Standort hat keine vollständige DATEV-Anbindung (Berater-/Mandantennummer fehlen).');
      return;
    }

    if (datevGateApplicable && !datevTokenOk) {
      setError('Für diesen Mandanten liegt kein gültiger DATEV-Token vor. Ohne die Mitarbeiter-Zuordnung aus DATEV kann das Paket nicht erstellt werden.');
      return;
    }

    setIsReleasing(true);
    setError(null);

    try {
      await onRelease(selectedContactId, selectedSender, customMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bereitstellung fehlgeschlagen');
      setIsReleasing(false);
    }
  };

  const handleMarkDoneWithoutSend = async () => {
    setMarkingDone(true);
    setError(null);
    try {
      await onMarkDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Frist konnte nicht als erledigt markiert werden');
      setMarkingDone(false);
    }
  };

  const isButtonDisabled =
    isReleasing ||
    markingDone ||
    contactsLoading ||
    contacts.length === 0 ||
    !selectedContactId ||
    accessCheckLoading ||
    !canAccess ||
    !hasToken ||
    (datevGateApplicable && (datevTokenLoading || !datevTokenOk)) ||
    senderIdentityUnconfirmed;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '24px',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#111827',
            margin: 0
          }}>
            OPPM Monatsübersicht QR-Code-Versand — {customer.company}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
            disabled={isReleasing || markingDone}
          >
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Info-Box: Periode und Ablauf */}
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#EFF6FF',
          borderRadius: '8px',
          border: '1px solid #BFDBFE'
        }}>
          <p style={{
            fontSize: '13px',
            color: '#1D4ED8',
            margin: 0,
            lineHeight: '1.5'
          }}>
            <strong>Monatsübersichten {getMonthName(periode.month)} {periode.year}:</strong> Sobald der
            externe Lauf die Monatsübersichten vollständig bereitgestellt hat, werden sie je Mitarbeiter
            mit QR-Code bestempelt, als ZIP im geschützten Downloadbereich abgelegt und der gewählte
            Empfänger benachrichtigt. Bis die Dateien vollständig vorliegen, bleibt die Frist auf
            &bdquo;beauftragt&ldquo; stehen; es geht kein unvollständiges Paket raus.
          </p>
          {(!customer.beraternummer || !customer.mandantennummer) && (
            <p style={{
              fontSize: '13px',
              color: '#991B1B',
              margin: '8px 0 0 0',
              lineHeight: '1.5',
              fontWeight: '500'
            }}>
              Achtung: Berater- und Mandantennummer müssen gesetzt sein, damit dieser Versand
              funktioniert.
            </p>
          )}
        </div>

        {/* DATEV-Token-Gate */}
        {datevGateApplicable && !datevTokenLoading && !datevTokenOk && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#FEF2F2',
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <ShieldAlert size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{
                fontSize: '14px',
                color: '#991B1B',
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Kein gültiger DATEV-Token
              </p>
              <p style={{
                fontSize: '13px',
                color: '#B91C1C',
                margin: 0,
                lineHeight: '1.4'
              }}>
                Für Berater {customer.beraternummer} / Mandant {customer.mandantennummer} liegt aktuell kein gültiger DATEV-Token vor. Ohne die Mitarbeiter-Zuordnung aus DATEV kann das Paket nicht erstellt werden.
                {datevProbeStatus ? ` (DATEV-Prüfstatus: ${datevProbeStatus})` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Empfänger-Auswahl */}
        {contactsLoading ? (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
              Empfänger werden geladen...
            </p>
          </div>
        ) : contacts.length === 0 ? (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#FEF2F2',
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <Users size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{
                fontSize: '14px',
                color: '#991B1B',
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Kein Empfänger verfügbar
              </p>
              <p style={{
                fontSize: '13px',
                color: '#B91C1C',
                margin: 0,
                lineHeight: '1.4'
              }}>
                Bitte legen Sie zuerst einen Ansprechpartner mit aktiviertem "Erhält OPPM-Monatsübersichten mit QR-Code" in den Kundenstammdaten an.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Empfänger
            </label>

            {contacts.length === 1 ? (
              <div style={{
                padding: '10px 12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px',
                border: '1px solid #D1D5DB'
              }}>
                <p style={{ fontSize: '14px', color: '#111827', margin: 0, fontWeight: '500' }}>
                  {customerContactService.getContactDisplayName(contacts[0])}
                  {contacts[0].rolle && (
                    <span style={{ fontWeight: 'normal', color: '#6B7280' }}> ({contacts[0].rolle})</span>
                  )}
                </p>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '2px 0 0 0' }}>
                  {contacts[0].email}{contacts[0].email_cc ? `, CC: ${contacts[0].email_cc}` : ''}
                </p>
              </div>
            ) : (
              <select
                value={selectedContactId || ''}
                onChange={(e) => {
                  setSelectedContactId(e.target.value || null);
                  setError(null);
                }}
                disabled={isReleasing || markingDone}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: selectedContactId ? '#374151' : '#9CA3AF',
                  backgroundColor: '#FFFFFF',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <option value="">Bitte Empfänger wählen...</option>
                {contacts.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {customerContactService.getContactDisplayName(contact)}
                    {contact.rolle ? ` (${contact.rolle})` : ''} – {contact.email}
                  </option>
                ))}
              </select>
            )}

            {selectedContactId && !accessCheckLoading && !canAccess && (
              <div style={{
                marginTop: '8px',
                padding: '10px 12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '6px',
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <Lock size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '13px', color: '#991B1B', margin: 0, lineHeight: '1.4' }}>
                  Dieser Empfänger hat weder ein Passwort gesetzt noch eine gültige Passwort-Einladung offen. Bitte senden Sie zuerst eine Passwort-Einladung in den Stammdaten des Ansprechpartners.
                </p>
              </div>
            )}

            {selectedContactId && canAccess && !hasToken && (
              <div style={{
                marginTop: '8px',
                padding: '10px 12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '6px',
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <Lock size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '13px', color: '#991B1B', margin: 0, lineHeight: '1.4' }}>
                  Diesem Empfänger fehlt der Zugangs-Token. Bitte sprechen Sie den Administrator an — der Token sollte beim Anlegen automatisch erzeugt werden.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Sender Selection */}
        {senderIdentityUnconfirmed && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#FEF3C7',
            borderRadius: '8px',
            border: '1px solid #F59E0B',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <ShieldAlert size={20} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{
                fontSize: '14px',
                color: '#92400E',
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Absender bitte bestätigen
              </p>
              <p style={{
                fontSize: '13px',
                color: '#B45309',
                margin: '0 0 10px 0',
                lineHeight: '1.4'
              }}>
                Der Absender konnte nicht automatisch über Ihre Anmeldung bestätigt werden — möglicherweise ist Ihre Sitzung abgelaufen.
                Voreingestellt ist <strong>{selectedSender?.fullName || 'der primäre Mitarbeiter'}</strong>.
                Die Bereitstellung ist gesperrt, bis Sie den Absender unten bewusst auswählen oder sich neu anmelden.
              </p>
              <button
                type="button"
                onClick={handleForceRelogin}
                disabled={isReleasing || markingDone}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#F59E0B',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: (isReleasing || markingDone) ? 'not-allowed' : 'pointer'
                }}
              >
                Neu anmelden
              </button>
            </div>
          </div>
        )}
        <SenderSelection
          senders={senders}
          selectedSender={selectedSender}
          onSenderChange={handleSenderChange}
          disabled={isReleasing || markingDone}
          loading={sendersLoading}
          error={sendersError}
          photoValidationErrors={photoValidationErrors}
        />

        {/* Custom Message Input */}
        <div style={{ marginBottom: '16px' }}>
          <CustomMessageInput
            value={customMessage}
            onChange={setCustomMessage}
            customer={customer}
            month={month}
            year={year}
            senderIdentity={selectedSender}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#FEF2F2',
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={16} color="#DC2626" />
            <span style={{ fontSize: '14px', color: '#DC2626' }}>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={handleMarkDoneWithoutSend}
            disabled={isReleasing || markingDone}
            title="Erledigt die Frist sofort, ohne einen Versand auszulösen"
            style={{
              padding: '8px 12px',
              backgroundColor: 'transparent',
              color: '#6B7280',
              border: '1px dashed #D1D5DB',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: (isReleasing || markingDone) ? 'not-allowed' : 'pointer'
            }}
          >
            {markingDone ? 'Wird markiert …' : 'Ohne Versand als erledigt markieren'}
          </button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={isReleasing || markingDone}
              style={{
                padding: '8px 16px',
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: (isReleasing || markingDone) ? 'not-allowed' : 'pointer'
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleRelease}
              disabled={isButtonDisabled}
              style={{
                padding: '8px 16px',
                backgroundColor: isButtonDisabled ? '#9CA3AF' : '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                cursor: isButtonDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              {isReleasing ? 'Wird beauftragt...' : 'Bereitstellen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonatsuebersichtQrVersandModal;
